import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GOAL_SPOTLIGHT_MS } from './goalCard';

/**
 * A tap on a goal in Program Görünümü opens Hedef Görünümü and lights that
 * goal's card for GOAL_SPOTLIGHT_MS, scrolled into view, so it is clear which
 * one is meant. The mobile twin is dijipo-mobile/hooks/useGoalSpotlight.ts.
 *
 * The page calls request(id) as it switches views, and marks each goal card
 * with data-goal-card={id}. `ready` says the list on screen holds the data the
 * request was made for (not a list still loading); the card is looked up after
 * every render until it shows up, or the page cancels.
 */
export function useGoalSpotlight(ready: boolean) {
  const [pending, setPending] = useState<string | null>(null);
  const [lit, setLit] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // After every render on purpose: the card appears whenever its list (or the
  // next Arşiv page) does. It only sets state once, when the card is found.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pending || !ready) return;
    const card = document.querySelector<HTMLElement>(`[data-goal-card="${pending}"]`);
    if (!card) return;
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPending(null);
    setLit(pending);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setLit(null), GOAL_SPOTLIGHT_MS);
  });

  const request = useCallback((id: unknown) => setPending(String(id)), []);
  const cancel = useCallback(() => setPending(null), []);
  return { pending, lit, request, cancel };
}

/** The lit card's ring and tint, over the card's own shadow and background. */
export function goalSpotlightStyle(lit: boolean, shadow: string, background?: string): React.CSSProperties {
  return {
    boxShadow: lit ? `0 0 0 3px #3973bf, 0 0 0 9px rgba(57,115,191,0.18), ${shadow}` : shadow,
    backgroundColor: lit ? '#f3f7fd' : background,
    transition: 'box-shadow 300ms ease-out, background-color 300ms ease-out',
  };
}
